/**
 * ArchitectTab - Architecture Definition & Build Management
 * 
 * Two-tab structure:
 * 1. Current Architecture - View active version + history with version dropdown
 * 2. Build Activity - Kanban-style active builds + activity log
 * 
 * Onboarding: Shows preset buttons for quick start (AWS, GCP, Azure, Vercel, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, Play, CheckCircle, Clock,
  Loader2, Activity, Hammer, ChevronDown, Plus,
  FileText, AlertTriangle, Cloud, Server, Zap, Box
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { infrastructureApi, deliveryConfigApi } from '../../services/api';
import CurrentArchitectureView from '../delivery/CurrentArchitectureView';
import BuildActivityView from '../delivery/BuildActivityView';
import NewBuildModal from '../delivery/NewBuildModal';

// Cloud provider presets for onboarding - includes both B2C and B2B defaults
const CLOUD_PRESETS = [
  {
    id: 'aws',
    name: 'AWS',
    description: 'Lambda, DynamoDB, API Gateway',
    color: 'from-orange-500 to-yellow-500',
    textColor: 'text-orange-600',
    bgColor: 'bg-orange-50 hover:bg-orange-100',
    borderColor: 'border-orange-200 hover:border-orange-400',
    // B2C defaults
    b2c: {
      environment: {
        cloud_provider: 'aws',
        region: 'us-east-1',
        compute: 'lambda',
        services: {
          database: 'dynamodb',
          storage: 's3',
          cache: 'elasticache_redis',
          queue: 'sqs',
          auth: 'cognito'
        },
        networking: {
          ingress: 'api_gateway',
          api_structure: 'path'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 0
        },
        secrets: {
          store: 'env_vars',
          naming: 'camelCase'
        }
      },
      project_dna: {
        compliance: [],
        delivery_lifecycle: 'direct',
        security_model: 'high_trust',
        architecture_style: 'monolith',
        quality_tier: 'prototype'
      }
    },
    // B2B defaults
    b2b: {
      environment: {
        cloud_provider: 'aws',
        region: 'us-east-1',
        compute: 'ecs_fargate',
        services: {
          database: 'rds_postgres',
          storage: 's3',
          cache: 'elasticache_redis',
          queue: 'sqs',
          auth: 'cognito'
        },
        networking: {
          ingress: 'api_gateway',
          api_structure: 'subdomain'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 1
        },
        secrets: {
          store: 'secrets_manager',
          naming: 'SCREAMING_SNAKE'
        }
      },
      project_dna: {
        compliance: ['soc2'],
        delivery_lifecycle: 'strict',
        security_model: 'zero_trust',
        architecture_style: 'monolith',
        quality_tier: 'production'
      }
    }
  },
  {
    id: 'gcp',
    name: 'Google Cloud',
    description: 'Cloud Run, Firestore, Pub/Sub',
    color: 'from-blue-500 to-green-500',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50 hover:bg-blue-100',
    borderColor: 'border-blue-200 hover:border-blue-400',
    b2c: {
      environment: {
        cloud_provider: 'gcp',
        region: 'us-central1',
        compute: 'cloud_run',
        services: {
          database: 'firestore',
          storage: 'gcs',
          cache: 'memorystore_redis',
          queue: 'pubsub',
          auth: 'firebase_auth'
        },
        networking: {
          ingress: 'cloud_run',
          api_structure: 'path'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 0
        },
        secrets: {
          store: 'env_vars',
          naming: 'camelCase'
        }
      },
      project_dna: {
        compliance: [],
        delivery_lifecycle: 'direct',
        security_model: 'high_trust',
        architecture_style: 'monolith',
        quality_tier: 'prototype'
      }
    },
    b2b: {
      environment: {
        cloud_provider: 'gcp',
        region: 'us-central1',
        compute: 'cloud_run',
        services: {
          database: 'cloud_sql_postgres',
          storage: 'gcs',
          cache: 'memorystore_redis',
          queue: 'pubsub',
          auth: 'cloud_identity'
        },
        networking: {
          ingress: 'cloud_load_balancing',
          api_structure: 'subdomain'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 1
        },
        secrets: {
          store: 'secrets_manager',
          naming: 'SCREAMING_SNAKE'
        }
      },
      project_dna: {
        compliance: ['soc2'],
        delivery_lifecycle: 'strict',
        security_model: 'zero_trust',
        architecture_style: 'monolith',
        quality_tier: 'production'
      }
    }
  },
  {
    id: 'azure',
    name: 'Azure',
    description: 'Functions, Cosmos DB, Service Bus',
    color: 'from-blue-600 to-cyan-500',
    textColor: 'text-sky-600',
    bgColor: 'bg-sky-50 hover:bg-sky-100',
    borderColor: 'border-sky-200 hover:border-sky-400',
    b2c: {
      environment: {
        cloud_provider: 'azure',
        region: 'eastus',
        compute: 'azure_functions',
        services: {
          database: 'cosmos_db',
          storage: 'azure_blob',
          cache: 'azure_redis',
          queue: 'storage_queues',
          auth: 'azure_ad_b2c'
        },
        networking: {
          ingress: 'api_management',
          api_structure: 'path'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 0
        },
        secrets: {
          store: 'env_vars',
          naming: 'camelCase'
        }
      },
      project_dna: {
        compliance: [],
        delivery_lifecycle: 'direct',
        security_model: 'high_trust',
        architecture_style: 'monolith',
        quality_tier: 'prototype'
      }
    },
    b2b: {
      environment: {
        cloud_provider: 'azure',
        region: 'eastus',
        compute: 'container_apps',
        services: {
          database: 'azure_postgres',
          storage: 'azure_blob',
          cache: 'azure_redis',
          queue: 'service_bus',
          auth: 'azure_ad'
        },
        networking: {
          ingress: 'front_door',
          api_structure: 'subdomain'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 1
        },
        secrets: {
          store: 'secrets_manager',
          naming: 'SCREAMING_SNAKE'
        }
      },
      project_dna: {
        compliance: ['soc2'],
        delivery_lifecycle: 'strict',
        security_model: 'zero_trust',
        architecture_style: 'monolith',
        quality_tier: 'production'
      }
    }
  },
  {
    id: 'vercel',
    name: 'Vercel',
    description: 'Edge Functions, Postgres, KV',
    color: 'from-gray-800 to-gray-600',
    textColor: 'text-gray-800',
    bgColor: 'bg-gray-50 hover:bg-gray-100',
    borderColor: 'border-gray-300 hover:border-gray-500',
    b2c: {
      environment: {
        cloud_provider: 'vercel',
        compute: 'vercel_serverless',
        services: {
          database: 'vercel_postgres',
          storage: 'vercel_blob',
          cache: 'vercel_kv',
          queue: 'qstash',
          auth: 'nextauth'
        },
        networking: {
          ingress: 'vercel_edge',
          api_structure: 'path'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 0
        },
        secrets: {
          store: 'env_vars',
          naming: 'camelCase'
        }
      },
      project_dna: {
        compliance: [],
        delivery_lifecycle: 'direct',
        security_model: 'high_trust',
        architecture_style: 'monolith',
        quality_tier: 'prototype'
      }
    },
    b2b: {
      environment: {
        cloud_provider: 'vercel',
        compute: 'vercel_serverless',
        services: {
          database: 'vercel_postgres',
          storage: 'vercel_blob',
          cache: 'vercel_kv',
          queue: 'qstash',
          auth: 'auth0'
        },
        networking: {
          ingress: 'vercel_edge',
          api_structure: 'subdomain'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 1
        },
        secrets: {
          store: 'secrets_manager',
          naming: 'SCREAMING_SNAKE'
        }
      },
      project_dna: {
        compliance: ['soc2'],
        delivery_lifecycle: 'strict',
        security_model: 'zero_trust',
        architecture_style: 'monolith',
        quality_tier: 'production'
      }
    }
  },
  {
    id: 'digitalocean',
    name: 'DigitalOcean',
    description: 'App Platform, Postgres, Spaces',
    color: 'from-blue-500 to-blue-400',
    textColor: 'text-blue-500',
    bgColor: 'bg-blue-50 hover:bg-blue-100',
    borderColor: 'border-blue-200 hover:border-blue-400',
    b2c: {
      environment: {
        cloud_provider: 'digitalocean',
        region: 'nyc1',
        compute: 'do_app_platform',
        services: {
          database: 'do_postgres',
          storage: 'do_spaces',
          cache: 'do_redis',
          queue: 'rabbitmq',
          auth: 'auth0'
        },
        networking: {
          ingress: 'do_app_platform',
          api_structure: 'path'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 0
        },
        secrets: {
          store: 'env_vars',
          naming: 'camelCase'
        }
      },
      project_dna: {
        compliance: [],
        delivery_lifecycle: 'direct',
        security_model: 'high_trust',
        architecture_style: 'monolith',
        quality_tier: 'prototype'
      }
    },
    b2b: {
      environment: {
        cloud_provider: 'digitalocean',
        region: 'nyc1',
        compute: 'do_kubernetes',
        services: {
          database: 'do_postgres',
          storage: 'do_spaces',
          cache: 'do_redis',
          queue: 'rabbitmq',
          auth: 'auth0'
        },
        networking: {
          ingress: 'do_load_balancer',
          api_structure: 'subdomain'
        },
        workflow: {
          branching: 'github_flow',
          pr_approvals: 1
        },
        secrets: {
          store: 'secrets_manager',
          naming: 'SCREAMING_SNAKE'
        }
      },
      project_dna: {
        compliance: ['soc2'],
        delivery_lifecycle: 'strict',
        security_model: 'zero_trust',
        architecture_style: 'monolith',
        quality_tier: 'production'
      }
    }
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'Define your own stack from scratch',
    color: 'from-purple-500 to-pink-500',
    textColor: 'text-purple-600',
    bgColor: 'bg-purple-50 hover:bg-purple-100',
    borderColor: 'border-purple-200 hover:border-purple-400',
    // Custom has minimal defaults, just tenancy-based DNA settings
    b2c: {
      environment: null, // No environment presets - user configures everything
      project_dna: {
        compliance: [],
        delivery_lifecycle: 'direct',
        security_model: 'high_trust',
        architecture_style: 'monolith',
        quality_tier: 'prototype'
      }
    },
    b2b: {
      environment: null, // No environment presets - user configures everything
      project_dna: {
        compliance: ['soc2'],
        delivery_lifecycle: 'strict',
        security_model: 'zero_trust',
        architecture_style: 'monolith',
        quality_tier: 'production'
      }
    }
  }
];

const ArchitectTab = ({ productId, product, canEdit, isOwner, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [hasArchitecture, setHasArchitecture] = useState(false);
  const [infraStatus, setInfraStatus] = useState({});
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  
  // Modal state
  const [showNewBuildModal, setShowNewBuildModal] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  // Onboarding selections
  const [selectedTenancy, setSelectedTenancy] = useState(null);

  // Load architecture status and versions
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [infraRes, configRes, versionsRes] = await Promise.all([
        infrastructureApi.getStatus(productId),
        deliveryConfigApi.getConfig(productId).catch(() => ({ data: {} })),
        infrastructureApi.getVersionHistory(productId).catch(() => ({ data: { versions: [] } }))
      ]);
      
      const status = infraRes.data?.infrastructure_status || {};
      setInfraStatus(status);
      
      // Check if architecture has been PUBLISHED (not just drafted)
      // Only exit onboarding when user has published or created a build
      const publishedVersion = status.published_version || 
                              status.current_version || 
                              configRes.data?.constraint_templates?.published_version || 0;
      
      // hasArchitecture is true ONLY if there's a published version > 0
      const hasPublishedArchitecture = publishedVersion > 0;
      setHasArchitecture(hasPublishedArchitecture);
      
      // Set versions - only show if there are published versions
      const versionList = versionsRes.data?.versions || [];
      
      if (hasPublishedArchitecture) {
        const currentVer = publishedVersion;
        const minorVer = status.minor_version || 0;
        
        // Create version list with current first
        const allVersions = [
          { version: currentVer, minor: minorVer, type: 'current', label: `v${currentVer}.${minorVer} (Current)` }
        ];
        
        // Add historical versions from activity
        versionList.forEach(v => {
          if (v.version !== currentVer) {
            allVersions.push({
              version: v.version,
              minor: v.minor || 0,
              type: v.type,
              label: `v${v.version}.${v.minor || 0}`,
              created_at: v.created_at
            });
          }
        });
        
        setVersions(allVersions);
        setSelectedVersion(`${currentVer}.${minorVer}`);
      } else {
        // No published version - just drafts
        setVersions([]);
        setSelectedVersion(null);
      }
      
    } catch (error) {
      console.error('Failed to load architect data:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      loadData();
    }
  }, [productId, loadData]);

  // Handle preset selection - apply preset and open modal
  const handlePresetClick = async (preset) => {
    setSelectedPreset(preset);
    
    // Get the tenancy-specific preset config
    const tenancyKey = selectedTenancy === 'b2b_multitenant' ? 'b2b' : 'b2c';
    const presetConfig = preset[tenancyKey];
    
    if (!presetConfig) {
      setShowNewBuildModal(true);
      return;
    }
    
    try {
      // Apply DNA settings (tenancy + other DNA defaults)
      const dnaUpdate = {
        tenancy_mode: selectedTenancy,
        ...presetConfig.project_dna
      };
      await deliveryConfigApi.updateDNA(productId, dnaUpdate);
      
      // Apply environment settings if preset has them
      if (presetConfig.environment) {
        await deliveryConfigApi.updateEnvironment(productId, presetConfig.environment);
      }
      
      toast.success(`${preset.name} preset applied`);
    } catch (error) {
      console.error('Failed to apply preset:', error);
      // Continue anyway - user can configure in modal
    }
    
    setShowNewBuildModal(true);
  };

  // Handle new build modal close
  const handleModalClose = (result) => {
    setShowNewBuildModal(false);
    setSelectedPreset(null);
    
    if (result?.success) {
      loadData();
      setSelectedTenancy(null); // Reset tenancy selection on success
      if (result.type === 'publish') {
        toast.success(`Architecture published as v${result.version}`);
      } else if (result.type === 'build') {
        toast.success('Build created and assigned');
        setActiveTab('activity');
      }
    }
  };

  // Handle version change
  const handleVersionChange = (versionStr) => {
    setSelectedVersion(versionStr);
    // The CurrentArchitectureView will react to the version prop change
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Onboarding View - No architecture defined yet
  if (!hasArchitecture) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="p-6">
          <div className="max-w-4xl mx-auto text-center space-y-8 py-12">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-blue-100">
              <Building2 className="w-10 h-10 text-purple-600" />
            </div>
            
            {/* Title */}
            <div className="space-y-3">
              <h1 className="text-3xl font-bold text-gray-900">
                Define Your Architecture
              </h1>
              <p className="text-lg text-gray-600 max-w-lg mx-auto">
                Select your target audience, then choose your cloud platform to get started.
              </p>
            </div>
            
            {/* Target Audience & Tenancy Selection */}
            <div className="max-w-xl mx-auto">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Target Audience & Tenancy</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSelectedTenancy('b2c')}
                  className={cn(
                    "p-4 rounded-xl border-2 text-left transition-all duration-200",
                    selectedTenancy === 'b2c'
                      ? "border-purple-500 bg-purple-50 shadow-md"
                      : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/50"
                  )}
                  data-testid="tenancy-b2c"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      selectedTenancy === 'b2c' ? "bg-purple-500" : "bg-gray-200"
                    )}>
                      <span className={cn(
                        "text-lg",
                        selectedTenancy === 'b2c' ? "text-white" : "text-gray-500"
                      )}>👤</span>
                    </div>
                    <div>
                      <p className={cn(
                        "font-semibold",
                        selectedTenancy === 'b2c' ? "text-purple-700" : "text-gray-700"
                      )}>Consumer App (B2C)</p>
                      <p className="text-xs text-gray-500">Single-tenant consumer application</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => setSelectedTenancy('b2b_multitenant')}
                  className={cn(
                    "p-4 rounded-xl border-2 text-left transition-all duration-200",
                    selectedTenancy === 'b2b_multitenant'
                      ? "border-purple-500 bg-purple-50 shadow-md"
                      : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/50"
                  )}
                  data-testid="tenancy-b2b"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      selectedTenancy === 'b2b_multitenant' ? "bg-purple-500" : "bg-gray-200"
                    )}>
                      <span className={cn(
                        "text-lg",
                        selectedTenancy === 'b2b_multitenant' ? "text-white" : "text-gray-500"
                      )}>🏢</span>
                    </div>
                    <div>
                      <p className={cn(
                        "font-semibold",
                        selectedTenancy === 'b2b_multitenant' ? "text-purple-700" : "text-gray-700"
                      )}>Enterprise Platform (B2B)</p>
                      <p className="text-xs text-gray-500">Multi-tenant SaaS platform</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Cloud Platform Selection */}
            <div className="max-w-3xl mx-auto">
              <h3 className={cn(
                "text-sm font-medium mb-3",
                selectedTenancy ? "text-gray-700" : "text-gray-400"
              )}>
                Cloud Platform
                {!selectedTenancy && <span className="ml-2 text-xs font-normal">(select tenancy first)</span>}
              </h3>
              <div className={cn(
                "grid grid-cols-2 md:grid-cols-3 gap-4",
                !selectedTenancy && "opacity-50 pointer-events-none"
              )}>
                {CLOUD_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handlePresetClick(preset)}
                    disabled={!selectedTenancy}
                    className={cn(
                      "p-5 rounded-xl border-2 text-left transition-all duration-200",
                      selectedTenancy && "hover:shadow-lg hover:-translate-y-0.5",
                      preset.bgColor,
                      preset.borderColor,
                      !selectedTenancy && "cursor-not-allowed"
                    )}
                    data-testid={`preset-${preset.id}`}
                  >
                    <div className="space-y-2">
                      {/* Provider Icon/Name */}
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center",
                          `bg-gradient-to-br ${preset.color}`
                        )}>
                          {preset.id === 'aws' && <span className="text-white font-bold text-xs">AWS</span>}
                          {preset.id === 'gcp' && <Cloud className="w-4 h-4 text-white" />}
                          {preset.id === 'azure' && <span className="text-white font-bold text-xs">Az</span>}
                          {preset.id === 'vercel' && <Zap className="w-4 h-4 text-white" />}
                          {preset.id === 'digitalocean' && <span className="text-white font-bold text-xs">DO</span>}
                          {preset.id === 'custom' && <Box className="w-4 h-4 text-white" />}
                        </div>
                        <span className={cn("font-semibold", preset.textColor)}>
                          {preset.name}
                        </span>
                      </div>
                      {/* Description */}
                      <p className="text-xs text-gray-500 line-clamp-2">
                        {preset.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Helper text */}
            <p className="text-sm text-gray-500">
              You&apos;ll be able to customize settings and publish constraints for your team&apos;s AI assistants.
            </p>
          </div>
        </div>
        
        {/* New Build Modal */}
        <NewBuildModal
          open={showNewBuildModal}
          onClose={handleModalClose}
          productId={productId}
          isOwner={isOwner}
          currentVersion={0}
          isFirstBuild={true}
          appliedPreset={selectedPreset}
        />
      </div>
    );
  }

  // Main Architecture View
  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Building2 className="w-6 h-6 text-purple-500" />
              <h2 className="text-2xl font-bold text-gray-900">Architecture</h2>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="current" className="gap-2" data-testid="tab-current-architecture">
                  <FileText className="w-4 h-4" />
                  Current Architecture
                </TabsTrigger>
                <TabsTrigger value="activity" className="gap-2" data-testid="tab-build-activity">
                  <Activity className="w-4 h-4" />
                  Build Activity
                  {infraStatus.status === 'in_progress' && (
                    <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                  )}
                </TabsTrigger>
              </TabsList>
              
              {/* Right side controls - only show on Current Architecture tab */}
              {activeTab === 'current' && (
                <div className="flex items-center gap-3">
                  {/* Version Dropdown - only show when there are published versions */}
                  {versions.length > 0 && (
                    <Select value={selectedVersion} onValueChange={handleVersionChange}>
                      <SelectTrigger className="w-40" data-testid="version-dropdown">
                        <SelectValue placeholder="Select version" />
                      </SelectTrigger>
                      <SelectContent>
                        {versions.map((v) => (
                          <SelectItem key={`${v.version}.${v.minor}`} value={`${v.version}.${v.minor}`}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {/* Start New Version Button */}
                  <Button
                    onClick={() => setShowNewBuildModal(true)}
                    disabled={!isOwner || infraStatus.status === 'in_progress'}
                    data-testid="start-new-version-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Start a New Version
                  </Button>
                </div>
              )}
            </div>

            {/* Current Architecture Tab Content */}
            <TabsContent value="current" className="mt-6">
              <CurrentArchitectureView
                productId={productId}
                version={selectedVersion}
                infraStatus={infraStatus}
                isOwner={isOwner}
              />
            </TabsContent>

            {/* Build Activity Tab Content */}
            <TabsContent value="activity" className="mt-6">
              <BuildActivityView
                productId={productId}
                infraStatus={infraStatus}
                isOwner={isOwner}
                onRefresh={loadData}
                currentVersion={infraStatus.published_version || 0}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* New Build Modal */}
      <NewBuildModal
        open={showNewBuildModal}
        onClose={handleModalClose}
        productId={productId}
        isOwner={isOwner}
        currentVersion={infraStatus.current_version || 0}
        isFirstBuild={false}
      />
    </div>
  );
};

export default ArchitectTab;
